import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useTranslation from '../../hooks/useTranslation';

/**
 * Boutons d'action sous une reponse OnX, fournis par pilot-chat (v10) :
 * { type: 'navigate'|'pdf'|'tasks', label, target, data }. Ephemeres : jamais
 * enregistres, ils disparaissent au rechargement.
 *
 * Seul `navigate` agit : `target` + data.project_id donnent une page de l'app.
 * `pdf` et `tasks` affichent « Bientot disponible » pendant 2 s.
 */
const TARGET_ROUTES = {
  project:          (id) => `/vue-projet/${id}`,
  budget:           (id) => `/project/${id}/budget`,
  extras:           (id) => `/project/${id}/extras`,
  contracts:        (id) => `/project/${id}/contracts`,
  // Les DP (demandes de paiement au client) vivent dans Facturation client.
  payment_requests: (id) => `/project/${id}/client-invoices`,
  // Les directives sont l'onglet « directives » de la page Extras.
  directives:       (id) => `/project/${id}/extras?tab=directives`,
  rfis:             (id) => `/project/${id}/rfi`,
  daily_log:        (id) => `/project/${id}/daily-log`,
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const OnXActionButtons = ({ actions }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [comingSoon, setComingSoon] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  if (!Array.isArray(actions) || actions.length === 0) return null;

  const handleClick = (action) => {
    if (action?.type === 'navigate') {
      const routeFn = TARGET_ROUTES?.[action?.target];
      const projectId = action?.data?.project_id;
      // project_id vient du modele : seulement un vrai identifiant dans l'URL.
      if (routeFn && typeof projectId === 'string' && UUID_RE.test(projectId)) {
        navigate(routeFn(projectId));
      }
      return;
    }
    // pdf, tasks, ou autre
    setComingSoon(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setComingSoon(false), 2000);
  };

  return (
    <div className="mt-2">
      <div className="flex flex-wrap gap-2">
        {actions.map((action, i) => (
          <button
            key={`${action?.type}-${action?.target}-${i}`}
            type="button"
            onClick={() => handleClick(action)}
            className="px-2.5 py-1 rounded-lg border border-gray-200 bg-white text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            {action?.label}
          </button>
        ))}
      </div>
      {comingSoon && (
        <p className="mt-1.5 text-xs text-gray-400">{t('onx.comingSoon')}</p>
      )}
    </div>
  );
};

export default OnXActionButtons;
